/**
 * Gentle Landing — Post-Session Closing Ritual
 *
 * Appears after Phase 5 (Choose), before Phase 6 (Close / summary).
 * Gives the nervous system a safe, embodied off-ramp after deep emotional work.
 *
 * Two screens:
 * 1. Practice — 30s somatic practice (extendable by tap, fully skippable)
 * 2. Closing   — short reflection message + "View My Session Summary"
 */
import { useState, useEffect, useRef } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { BACK_DEPTH, useSessionBack } from '../../lib/sessionBack'
import { useTranslation } from 'react-i18next'

interface GentleLandingProps {
  firstName?: string
  onComplete: () => void
}

const DEFAULT_SECONDS = 30

function BreathCircle({ active }: { active: boolean }) {
  return (
    <div className="flex items-center justify-center my-8">
      <div style={{ position: 'relative', width: 100, height: 100 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(155,114,192,0.12) 0%, transparent 70%)',
          animation: active ? 'gentleBreathe 5s ease-in-out infinite' : 'none',
        }} />
        <div style={{
          position: 'absolute', inset: '14px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(155,114,192,0.25) 0%, rgba(155,114,192,0.08) 70%)',
          animation: active ? 'gentleBreathe 5s ease-in-out infinite 0.8s' : 'none',
        }} />
        <div style={{
          position: 'absolute', inset: '30px', borderRadius: '50%',
          background: '#9B72C0',
          opacity: 0.35,
          animation: active ? 'gentleBreathe 5s ease-in-out infinite 1.6s' : 'none',
        }} />
      </div>
      <style>{`
        @keyframes gentleBreathe {
          0%, 100% { transform: scale(0.9); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}

export default function GentleLanding({ firstName: _firstName = '', onComplete }: GentleLandingProps) {
  const { t } = useTranslation()
  // The three soothing practices, from the catalogue. They were English text in
  // this file and rendered as is, so a Polish session showed them in English.
  const practices = t('gentleLanding.practices', { returnObjects: true }) as string[]
  const [screen, setScreen] = useState<'practice' | 'closing'>('practice')
  useScreenId(screen === 'practice' ? 'choose.landing.practice' : 'choose.landing.closing')
  // Back from the closing message returns to the practice screen. From the
  // practice screen there is nothing earlier here, so Back moves on to Choose.
  useSessionBack(() => {
    if (screen !== 'closing') return 'first'
    setScreen('practice')
    return 'handled'
  }, BACK_DEPTH.step)
  const [secondsLeft, setSecondsLeft] = useState(DEFAULT_SECONDS)
  const [timerRunning, setTimerRunning] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Count down; auto-advance to closing screen when done
  useEffect(() => {
    if (!timerRunning) return
    intervalRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(intervalRef.current!)
          setTimerRunning(false)
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(intervalRef.current!)
  }, [timerRunning])

  function handleExtend() {
    setSecondsLeft(s => s + 30)
    setTimerRunning(true)
  }

  function handleReadyNow() {
    clearInterval(intervalRef.current!)
    setTimerRunning(false)
    setScreen('closing')
  }

  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

        {/* ── Practice screen ──────────────────────────────────────────────── */}
        {screen === 'practice' && (
          <div className="flex-1 flex flex-col message-appear">
            <p className="text-xs font-medium uppercase tracking-widest mb-8" style={{ color: '#9B72C0' }}>
              {t('gentleLanding.title')}
            </p>

            <p className="font-display text-xl leading-snug mb-3"
              style={{ color: '#1C1128', fontWeight: 400 }}>
              {t('gentleLanding.beautifulWork')}
            </p>
            <p className="text-sm leading-relaxed mb-8" style={{ color: '#6B5F7A' }}>
              {t('gentleLanding.beforeWeClose')} {t('gentleLanding.beforeWeCloseTail')}
            </p>

            <p className="text-sm font-medium mb-4" style={{ color: '#6B5F7A' }}>
              {t('gentleLanding.chooseSoothing')}
            </p>
            <div className="space-y-4 mb-6">
              {practices.map((p, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#9B72C0' }} />
                  <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{p}</p>
                </div>
              ))}
            </div>

            <BreathCircle active={timerRunning} />

            {/* Timer */}
            {secondsLeft > 0 ? (
              <div className="text-center mb-6">
                <p className="text-2xl font-mono" style={{ color: '#9B72C0' }}>
                  {secondsLeft}
                </p>
                <p className="text-xs mt-1" style={{ color: '#C4B5D4' }}>seconds</p>
              </div>
            ) : (
              <div className="text-center mb-6 message-appear">
                <p className="text-sm" style={{ color: '#9B72C0' }}>
                  {t('gentleLanding.takeAsLong')}
                </p>
              </div>
            )}

            <div className="flex-1" />

            <div className="flex flex-col gap-3">
              {secondsLeft > 0 ? (
                <>
                  <button
                    onClick={handleExtend}
                    className="w-full py-3 rounded-xl text-sm"
                    style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
                  >
                    {t('gentleLanding.thirtyMore')}
                  </button>
                  <button
                    onClick={handleReadyNow}
                    className="w-full py-3 rounded-xl text-sm"
                    style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
                  >
                    {t('gentleLanding.imReadyNow')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setScreen('closing')}
                  className="w-full py-4 rounded-xl font-medium tracking-wide message-appear"
                  style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                >
                  {t('gentleLanding.imReady')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Closing screen ───────────────────────────────────────────────── */}
        {screen === 'closing' && (
          <div className="flex-1 flex flex-col items-center justify-center message-appear text-center">

            {/* Soft glow */}
            <div className="mb-10"
              style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(155,114,192,0.3) 0%, rgba(155,114,192,0) 70%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#9B72C0', opacity: 0.7 }} />
            </div>

            <p className="font-display text-xl leading-relaxed mb-6 max-w-xs"
              style={{ color: '#1C1128', fontStyle: 'italic', fontWeight: 400 }}>
              {t('gentleLanding.courageous')}
            </p>

            <p className="text-sm leading-relaxed mb-12 max-w-xs" style={{ color: '#6B5F7A' }}>
              {t('gentleLanding.settleGift')}
            </p>

            <button
              onClick={onComplete}
              className="w-full max-w-xs py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('gentleLanding.viewSummary')}
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
